import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonDatetime,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { arrowBack, addCircleOutline, createOutline, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import LimpiezasService, {
  AsignacionPayload,
  LimpiezaAsignacion,
  LimpiezaDetalleResponse,
  LimpiezaPlanificacionDetalle,
  OperarioDisponible,
} from "src/services/limpiezasProfundas.service";
import "./LimpiezasProfundasDetalle.css";

interface RouteParams {
  planId: string;
}

interface FormState {
  id?: number;
  operario?: OperarioDisponible;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  sector: string;
  area: string;
  cantidad: string;
}

const buildInitialFormState = (): FormState => ({
  fecha: new Date().toISOString().split("T")[0],
  horaInicio: "",
  horaFin: "",
  sector: "",
  area: "",
  cantidad: "",
});

const LimpiezasProfundasDetalle: React.FC = () => {
  const { planId } = useParams<RouteParams>();
  const history = useHistory();
  const [detalle, setDetalle] = useState<LimpiezaPlanificacionDetalle | null>(null);
  const [asignaciones, setAsignaciones] = useState<LimpiezaAsignacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [busquedaOperario, setBusquedaOperario] = useState("");
  const [operarios, setOperarios] = useState<OperarioDisponible[]>([]);
  const [buscandoOperarios, setBuscandoOperarios] = useState(false);
  const [form, setForm] = useState<FormState>(buildInitialFormState());
  const [otNumero, setOtNumero] = useState("");
  const [present] = useIonToast();

  const handleFechaChange = (value?: string | null) => {
    if (!value) return;
    const fecha = value.includes("T") ? value.split("T")[0] : value.slice(0, 10);
    setForm((prev) => ({ ...prev, fecha }));
  };

  const handleHoraChange = (field: "horaInicio" | "horaFin") => (value?: string | null) => {
    if (!value) return;
    let hora = value;
    if (hora.includes("T")) {
      const split = hora.split("T")[1];
      hora = split.slice(0, 5);
    } else if (hora.length > 5) {
      hora = hora.slice(0, 5);
    }
    setForm((prev) => ({ ...prev, [field]: hora }));
  };

  const cargarDetalle = async () => {
    setLoading(true);
    try {
      const resp: LimpiezaDetalleResponse = await LimpiezasService.fetchPlanificacionDetalle(Number(planId));
      setDetalle(resp.planificacion);
      setAsignaciones(resp.asignaciones || []);
      setOtNumero(resp.planificacion?.orden_trabajo || "");
    } catch (error) {
      console.error(error);
      present({
        message: "No se pudo cargar la información de la planificación.",
        duration: 2500,
        color: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const editable = detalle?.editable !== false;

  const handleBuscarOperarios = async () => {
    if (!detalle?.ps_id) {
      present({ message: "No se reconoce el Punto de Servicio de la planificación.", duration: 2000, color: "warning" });
      return;
    }
    if (busquedaOperario.trim().length < 2) {
      present({ message: "Ingresa al menos 2 caracteres para la búsqueda.", duration: 2000, color: "medium" });
      return;
    }
    try {
      setBuscandoOperarios(true);
      const resultados = await LimpiezasService.buscarOperarios(detalle.ps_id, busquedaOperario.trim());
      setOperarios(resultados);
    } catch (error) {
      console.error(error);
      present({ message: "No se pudo obtener la lista de operarios.", duration: 2000, color: "danger" });
    } finally {
      setBuscandoOperarios(false);
    }
  };

  const seleccionarOperario = (operario: OperarioDisponible) => {
    setForm((prev) => ({
      ...prev,
      operario,
    }));
    setOperarios([]);
    setBusquedaOperario(`${operario.legajo} - ${operario.nombre}`);
  };

  const limpiarFormulario = () => {
    setForm(buildInitialFormState());
    setBusquedaOperario("");
    setOperarios([]);
  };

  const handleEditar = (asignacion: LimpiezaAsignacion) => {
    setForm({
      id: asignacion.id,
      operario: {
        id: asignacion.operario_id,
        legajo: asignacion.operario_legajo,
        nombre: asignacion.operario_nombre,
      },
      fecha: asignacion.fecha_ejecucion ? asignacion.fecha_ejecucion.split("T")[0] : buildInitialFormState().fecha,
      horaInicio: asignacion.hora_inicio || "",
      horaFin: asignacion.hora_fin || "",
      sector: asignacion.sector || "",
      area: asignacion.area || "",
      cantidad: asignacion.cantidad != null ? String(asignacion.cantidad) : "",
    });
    setBusquedaOperario(`${asignacion.operario_legajo} - ${asignacion.operario_nombre}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleGuardar = async () => {
    if (!editable) {
      present({ message: "La planificación ya se encuentra finalizada.", duration: 2000, color: "warning" });
      return;
    }
    if (!form.operario) {
      present({ message: "Selecciona un operario.", duration: 2000, color: "medium" });
      return;
    }
    if (!form.fecha || !form.horaInicio || !form.horaFin) {
      present({ message: "Debes indicar la fecha y horario de ejecución.", duration: 2000, color: "medium" });
      return;
    }

    let cantidadValor: number | undefined;
    if (form.cantidad) {
      const parsed = Number(form.cantidad);
      if (Number.isNaN(parsed)) {
        present({ message: "La cantidad debe ser numérica.", duration: 2000, color: "medium" });
        return;
      }
      cantidadValor = parsed;
    }

    const payload: AsignacionPayload = {
      operario_id: form.operario.id,
      fecha_ejecucion: form.fecha,
      hora_inicio: form.horaInicio,
      hora_fin: form.horaFin,
      sector: form.sector || undefined,
      area: form.area || undefined,
      cantidad: cantidadValor,
    };

    try {
      setSaving(true);
      let response;
      if (form.id) {
        response = await LimpiezasService.actualizarAsignacion(Number(planId), form.id, payload);
      } else {
        response = await LimpiezasService.crearAsignacion(Number(planId), payload);
      }
      setAsignaciones(response.asignaciones || []);
      present({ message: "Asignación guardada correctamente.", duration: 2000, color: "success" });
      limpiarFormulario();
      await cargarDetalle();
    } catch (error) {
      console.error(error);
      present({ message: "No se pudo guardar la asignación.", duration: 2000, color: "danger" });
    } finally {
      setSaving(false);
    }
  };



  const handleEliminar = async (asignacion: LimpiezaAsignacion) => {
    if (!editable) {
      present({ message: "La planificación ya se encuentra finalizada.", duration: 2000, color: "warning" });
      return;
    }
    const confirmar = window.confirm(`¿Eliminar la asignación de ${asignacion.operario_nombre}?`);
    if (!confirmar) return;
    try {
      setSaving(true);
      const response = await LimpiezasService.eliminarAsignacion(Number(planId), asignacion.id);
      setAsignaciones(response.asignaciones || []);
      present({ message: "Asignación eliminada.", duration: 1500, color: "success" });
      await cargarDetalle();
    } catch (error) {
      console.error(error);
      present({ message: "No se pudo eliminar la asignación.", duration: 2000, color: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizar = async () => {
    if (!editable) {
      present({ message: "La planificación ya fue finalizada.", duration: 2000, color: "medium" });
      return;
    }
    if (!otNumero.trim()) {
      present({ message: "Ingresa el número de OT para finalizar.", duration: 2000, color: "medium" });
      return;
    }
    try {
      setFinalizando(true);
      await LimpiezasService.finalizarPlanificacion(Number(planId), {
        orden_trabajo: otNumero.trim(),
      });
      present({ message: "Planificación finalizada correctamente.", duration: 2000, color: "success" });
      await cargarDetalle();
    } catch (error) {
      console.error(error);
      present({ message: "No se pudo finalizar la planificación.", duration: 2000, color: "danger" });
    } finally {
      setFinalizando(false);
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar color="primary">
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Detalle de Planificación</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="lp-detalle-loading">
          <IonSpinner />
        </IonContent>
      </IonPage>
    );
  }

  if (!detalle) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar color="primary">
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Detalle de Planificación</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="lp-detalle-loading">
          <p>No se encontró la planificación.</p>
          <IonButton onClick={() => history.goBack()} fill="clear" color="medium">
            <IonIcon slot="start" icon={arrowBack} />
            Volver
          </IonButton>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Detalle de Planificación</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.goBack()} fill="clear">
              <IonIcon icon={arrowBack} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <section className="lp-detalle-section">
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{detalle.ps_nombre}</IonCardTitle>
              <IonCardSubtitle>{detalle.ps_codigo}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="lp-detalle-grid">
                <div>
                  <span>Estado:</span>
                  <strong>{detalle.estado}</strong>
                </div>
                <div>
                  <span>Fecha:</span>
                  <strong>{detalle.fecha_label || (detalle.fecha ? new Date(detalle.fecha).toLocaleDateString() : "-")}</strong>
                </div>
                <div>
                  <span>Tipo de frecuencia:</span>
                  <strong>{detalle.frecuencia_label || detalle.frecuencia}</strong>
                </div>
                <div>
                  <span>Tipo de hora:</span>
                  <strong>{detalle.tipo_hora || "Sin dato"}</strong>
                </div>
                <div>
                  <span>Tipo de limpieza:</span>
                  <strong>{detalle.tipo_lavado}</strong>
                </div>
                <div>
                  <span>Perfil requerido:</span>
                  <strong>{detalle.perfil_requerido || "Sin perfil"}</strong>
                </div>
                <div>
                  <span>Área / Cantidad:</span>
                  <strong>
                    {detalle.area || "-"} / {detalle.cantidad ?? "-"}
                  </strong>
                </div>
                <div>
                  <span>Sector:</span>
                  <strong>{detalle.sector || "-"}</strong>
                </div>
                <div>
                  <span>Cupo planificado:</span>
                  <strong>{detalle.cupo_planificado}</strong>
                </div>
                <div>
                  <span>Cupo utilizada:</span>
                  <strong>{detalle.cupo_utilizada || "0:00"}</strong>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{form.id ? "Editar asignación" : "Nueva asignación"}</IonCardTitle>
              {!editable && <IonCardSubtitle>La planificación fue finalizada</IonCardSubtitle>}
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonLabel position="stacked">Buscar operario (nombre o legajo)</IonLabel>
                <IonInput
                  value={busquedaOperario}
                  onIonChange={(e) => setBusquedaOperario(e.detail.value || "")}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonButton
                expand="block"
                color="primary"
                onClick={handleBuscarOperarios}
                disabled={!editable || saving}
              >
                Buscar
              </IonButton>
              {buscandoOperarios && (
                <div className="lp-detalle-loading">
                  <IonSpinner />
                </div>
              )}
              {!buscandoOperarios && operarios.length > 0 && (
                <IonList className="lp-operarios-list">
                  {operarios.map((op) => (
                    <IonItem key={op.id} button onClick={() => seleccionarOperario(op)}>
                      <IonLabel>
                        <strong>{op.nombre}</strong>
                        <p>Legajo: {op.legajo}</p>
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              )}
              <IonItem>
                <IonLabel position="stacked">Fecha de ejecución</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  doneText="Aceptar"
                  cancelText="Cancelar"
                  value={form.fecha ? `${form.fecha}T00:00:00` : undefined}
                  onIonChange={(e) => handleFechaChange(e.detail.value)}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Hora de inicio</IonLabel>
                <IonDatetime
                  displayFormat="HH:mm"
                  pickerFormat="HH:mm"
                  doneText="Aceptar"
                  cancelText="Cancelar"
                  value={form.horaInicio ? `1970-01-01T${form.horaInicio}` : undefined}
                  onIonChange={(e) => handleHoraChange("horaInicio")(e.detail.value)}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Hora de fin</IonLabel>
                <IonDatetime
                  displayFormat="HH:mm"
                  pickerFormat="HH:mm"
                  doneText="Aceptar"
                  cancelText="Cancelar"
                  value={form.horaFin ? `1970-01-01T${form.horaFin}` : undefined}
                  onIonChange={(e) => handleHoraChange("horaFin")(e.detail.value)}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Sector</IonLabel>
                <IonInput
                  value={form.sector}
                  onIonChange={(e) => setForm((prev) => ({ ...prev, sector: e.detail.value || "" }))}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Área</IonLabel>
                <IonInput
                  value={form.area}
                  onIonChange={(e) => setForm((prev) => ({ ...prev, area: e.detail.value || "" }))}
                  disabled={!editable || saving}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Cantidad</IonLabel>
                <IonInput
                  type="number"
                  value={form.cantidad}
                  onIonChange={(e) => setForm((prev) => ({ ...prev, cantidad: e.detail.value || "" }))}
                  disabled={!editable || saving}
                />
              </IonItem>
              <div className="lp-form-actions">
                <IonButton onClick={handleGuardar} disabled={!editable || saving}>
                  <IonIcon slot="start" icon={form.id ? createOutline : addCircleOutline} />
                  {form.id ? "Actualizar" : "Agregar"}
                </IonButton>
                {form.id && (
                  <IonButton fill="clear" color="medium" onClick={limpiarFormulario} disabled={saving}>
                    Cancelar
                  </IonButton>
                )}
              </div>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Asignaciones realizadas</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {asignaciones.length === 0 && <p className="lp-empty">Aún no se registraron asignaciones.</p>}
              <IonList>
                {asignaciones.map((asignacion) => (
                  <IonItem key={asignacion.id} lines="full">
                    <IonLabel>
                      <strong>{asignacion.operario_nombre}</strong>
                      <p>Legajo: {asignacion.operario_legajo}</p>
                      <p>Fecha: {asignacion.fecha_ejecucion ? new Date(asignacion.fecha_ejecucion).toLocaleDateString() : "-"}</p>
                      <p>
                        Horario:{" "}
                        {asignacion.hora_inicio || "-"} - {asignacion.hora_fin || "-"}
                      </p>
                      <p>Horas: {asignacion.horas}</p>
                      <p>
                        Sector / Área: {(asignacion.sector || "-")} / {(asignacion.area || "-")}
                      </p>
                      <p>Cantidad: {asignacion.cantidad ?? "-"}</p>
                    </IonLabel>
                    {editable && (
                      <div className="lp-actions">
                        <IonButton
                          fill="clear"
                          color="primary"
                          size="small"
                          onClick={() => handleEditar(asignacion)}
                        >
                          <IonIcon icon={createOutline} />
                        </IonButton>
                        <IonButton
                          fill="clear"
                          color="danger"
                          size="small"
                          onClick={() => handleEliminar(asignacion)}
                        >
                          <IonIcon icon={trashOutline} />
                        </IonButton>
                      </div>
                    )}
                  </IonItem>
                ))}
              </IonList>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Finalizar planificación</IonCardTitle>
              <IonCardSubtitle>Ingresa el número de OT para finalizar el seguimiento</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonLabel position="stacked">Número de OT</IonLabel>
                <IonInput value={otNumero} onIonChange={(e) => setOtNumero(e.detail.value || "")} />
              </IonItem>
              <IonButton
                expand="block"
                color="primary"
                onClick={handleFinalizar}
                disabled={!editable || finalizando}
              >
                Finalizar
              </IonButton>
            </IonCardContent>
          </IonCard>
        </section>
      </IonContent>
    </IonPage>
  );
};

export default LimpiezasProfundasDetalle;
